/**
 * HowItSaves — "How CARTIE saves you money — explained simply"
 *
 * The user said most people don't understand what CARTIE does. This page
 * fixes that with FIVE plain-English explanations + concrete dollar examples.
 * No jargon, no marketing fluff. Each saving mechanism has:
 *   - A simple analogy (e.g. "Same as Google caching your search results")
 *   - The math (e.g. "Without CARTIE: $X.  With CARTIE: $Y.  Save $Z.")
 *   - Where to see it in your dashboard
 *
 * Public, no auth. Designed for procurement teams + curious founders.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Recycle, Sparkles, Layers, Radio, GitPullRequest,
  ArrowRight, CheckCircle2, DollarSign, Clock, Zap,
} from 'lucide-react';

const MECHANISMS = [
  {
    id: 'prompt-cache',
    icon: Recycle,
    color: 'emerald',
    title: '1. Prompt Cache — "Don\'t pay twice for the same answer"',
    plain_english: (
      <>
        When your app asks an AI the same question more than once, CARTIE
        remembers the answer and returns it instantly — <strong>without paying
        OpenAI / Anthropic / Gemini / Groq / Mistral again</strong>.
      </>
    ),
    analogy: (
      <>
        Like Google caching your search results. The first search costs you
        compute; the second is free.
      </>
    ),
    example: {
      without: 'Your support bot answers "What\'s the refund policy?" 5,000 times/day × $0.012/call = $60/day = **$1,800/month**',
      with:    'First call: $0.012.  Calls 2–5,000 served from cache: $0.  Total: **$0.012/day = $0.36/month**',
      savings: '$1,799.64/month per repeated question',
    },
    where: '/llm-proxy → Savings tab → "Prompt cache" KPI',
  },
  {
    id: 'streaming-cache',
    icon: Radio,
    color: 'sky',
    title: '2. Streaming Cache — "Save on chat-style responses too"',
    plain_english: (
      <>
        Most AI chats stream the answer word-by-word for a typewriter effect.
        Other caches give up here. CARTIE captures every chunk on the first
        call and replays them on identical questions — the typewriter still works,
        but you pay $0 the second time.
      </>
    ),
    analogy: (
      <>
        Like recording a TV show on your DVR. Live the first time, instant
        replay every time after.
      </>
    ),
    example: {
      without: 'Real-time GPT-4 chat at 1,000 streaming requests/day × $0.04/call = **$1,200/month**',
      with:    'Cache hit rate at 60% → 600 served from cache, 400 upstream → **$480/month**',
      savings: '$720/month for a single chat workflow',
    },
    where: '/llm-proxy → Savings tab → "Streaming cache" KPI (NEW)',
  },
  {
    id: 'embedding-cache',
    icon: Layers,
    color: 'violet',
    title: '3. RAG Embedding Cache — "Don\'t re-vectorize the same document"',
    plain_english: (
      <>
        AI apps that search documents (RAG) convert text into numerical
        &ldquo;vectors&rdquo; so the AI can find similar passages. CARTIE caches these
        vectors so you only pay to convert each document once — not every
        time someone searches it.
      </>
    ),
    analogy: (
      <>
        Like a library indexing books once instead of re-indexing them every
        time someone walks in.
      </>
    ),
    example: {
      without: 'Your RAG chatbot embeds 10,000 chunks/day × $0.00002/chunk = **$6/day = $180/month**.  Multiply by 50 chatbots = **$9,000/month**',
      with:    'After day 1, cache covers 95% of embeddings → **$450/month total**',
      savings: '$8,550/month across the fleet · ~40% of OpenAI bill for RAG apps',
    },
    where: '/llm-proxy → Savings tab → "Embedding cache" KPI (NEW · v35.14)',
  },
  {
    id: 'model-cascade',
    icon: Sparkles,
    color: 'amber',
    title: '4. Model Cascade — "Use a cheaper AI when the cheaper AI is enough"',
    plain_english: (
      <>
        Most AI calls use the most expensive model &ldquo;just to be safe.&rdquo; CARTIE
        figures out the simplest task class for each prompt and routes it to
        the cheapest model that can handle it — Claude Haiku for classification,
        GPT-4o-mini for summarization, only escalating to GPT-4 when the task
        actually demands it.
      </>
    ),
    analogy: (
      <>
        Like sending an intern to fetch coffee instead of the CEO.
        Both get the job done; one costs $1 and the other costs $1,000.
      </>
    ),
    example: {
      without: '1,000 classification calls × GPT-4-Turbo ($0.03/call) = **$30/day = $900/month**',
      with:    'Cascade detects "classification" → routes to Haiku ($0.0008/call) = **$24/month**',
      savings: '$876/month per classification workflow · 97% reduction',
    },
    where: '/llm-proxy → Savings tab → "Cascade" KPI',
  },
  {
    id: 'compactor',
    icon: Zap,
    color: 'rose',
    title: '5. Prompt Compactor — "Strip the bloat from your system prompts"',
    plain_english: (
      <>
        System prompts often include long instructions that get sent on every
        single call. CARTIE removes redundant whitespace, duplicate
        instructions, and stale boilerplate before the prompt hits the
        provider — silently reducing your token bill 10-15%.
      </>
    ),
    analogy: (
      <>
        Like zipping a file before you email it.  Same content, fewer bytes
        on the wire.
      </>
    ),
    example: {
      without: '1,200-token system prompt × 100k calls/day × $0.01/1k input = **$36/day = $1,080/month**',
      with:    'Compacted to 720 tokens (40% smaller) → **$648/month**',
      savings: '$432/month per high-volume prompt',
    },
    where: '/llm-proxy → Savings tab → "Tokens stripped" KPI',
  },
  {
    id: 'cloud-engines',
    icon: DollarSign,
    color: 'cyan',
    title: '6. The 26 Cloud Engines — "Catch the waste before it ships"',
    plain_english: (
      <>
        For AWS, Azure, and GCP: 26 always-on engines watch your cloud bill
        for idle resources, oversized databases, untagged spend, abandoned
        dev environments, log-retention runaway, spot-eligibility, and 20+
        other waste patterns.  Each finding becomes a fix you can ship.
      </>
    ),
    analogy: (
      <>
        Like having 26 specialists each watching one specific way your cloud
        bill could be leaking money — 24/7, never on vacation.
      </>
    ),
    example: {
      without: 'Typical $20k/mo AWS bill — 15-25% wasted on idle/oversized/untagged = **$3,000–$5,000/mo lost**',
      with:    'CARTIE detects + (with your approval) ships the fix as a GitHub PR → reclaimed in 1-3 days',
      savings: '$3,000–$5,000/month on a $20k/month spend · scales linearly',
    },
    where: '/dashboard → Cost Intelligence Dashboard + /recommendations',
  },
  {
    id: 'code-tracer',
    icon: GitPullRequest,
    color: 'emerald',
    title: '7. Code Tracer + PR Auto-Fixer — "Detect → Fix → Done"',
    plain_english: (
      <>
        When CARTIE detects a cost spike, it pinpoints the exact line of code
        responsible (e.g. <code className="font-mono text-emerald-300 bg-slate-950 px-1.5 py-0.5 rounded text-xs">claude-opus-4-5</code> in
        <code className="font-mono text-emerald-300 bg-slate-950 px-1.5 py-0.5 rounded text-xs">summarizer.py:42</code>),
        opens a draft GitHub PR with the suggested fix, and your engineer just
        clicks &ldquo;merge.&rdquo;
      </>
    ),
    analogy: (
      <>
        Like a really diligent intern who finds bugs in your code, writes the
        fix, opens a PR — and waits for your nod.
      </>
    ),
    example: {
      without: 'Cost spike noticed 14 days later in monthly bill review. Engineer hunts for the cause for 2-4 hours. Maybe ships the fix next sprint.',
      with:    'CARTIE detects spike → draft PR in your repo in 60 seconds → engineer reviews + merges in 5 minutes → savings booked the same day',
      savings: 'Hours of engineering time + faster realized savings (typically 14-day delta)',
    },
    where: '/code-tracer + /policies/automation',
  },
];

const colorMap = {
  emerald: { ring: 'ring-emerald-500/30', iconBg: 'bg-emerald-500/15 text-emerald-300', accent: 'text-emerald-300', border: 'border-emerald-500/30' },
  sky:     { ring: 'ring-sky-500/30',     iconBg: 'bg-sky-500/15 text-sky-300',         accent: 'text-sky-300',     border: 'border-sky-500/30' },
  violet:  { ring: 'ring-violet-500/30',  iconBg: 'bg-violet-500/15 text-violet-300',   accent: 'text-violet-300',  border: 'border-violet-500/30' },
  amber:   { ring: 'ring-amber-500/30',   iconBg: 'bg-amber-500/15 text-amber-300',     accent: 'text-amber-300',   border: 'border-amber-500/30' },
  rose:    { ring: 'ring-rose-500/30',    iconBg: 'bg-rose-500/15 text-rose-300',       accent: 'text-rose-300',    border: 'border-rose-500/30' },
  cyan:    { ring: 'ring-cyan-500/30',    iconBg: 'bg-cyan-500/15 text-cyan-300',       accent: 'text-cyan-300',    border: 'border-cyan-500/30' },
};

export default function HowItSaves() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Helmet>
        <title>How CARTIE saves you money — explained simply</title>
        <meta name="description" content="Plain-English explainer of how CARTIE cuts your cloud + AI token bill 20-40%. Prompt cache, embedding cache, model cascade, code tracer, and the 26 cloud engines — with concrete dollar examples for each." />
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/5 px-4 py-3 flex items-center justify-between sticky top-0 z-30 bg-slate-950/90 backdrop-blur">
        <Link to="/" className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="how-it-saves-logo">
          ← CARTIE
        </Link>
        <div className="flex items-center gap-4 text-xs font-semibold text-slate-400">
          <Link to="/how-it-works" className="hover:text-emerald-300" data-testid="how-it-saves-nav-autonomy">Autonomy tiers</Link>
          <Link to="/guarantee" className="hover:text-emerald-300" data-testid="how-it-saves-nav-guarantee">3× Guarantee</Link>
          <Link to="/login" className="hover:text-emerald-300" data-testid="how-it-saves-nav-signin">Sign in</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 py-14 max-w-3xl mx-auto" data-testid="how-it-saves-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-5">
          For the curious · No jargon
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" data-testid="how-it-saves-h1">
          How CARTIE saves you money,{' '}
          <span className="text-emerald-400">explained like you&apos;re a normal human.</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
          Seven different ways. Each one is a thing your AI or cloud bill is
          quietly leaking money on. Each one has a dollar example so you can
          tell if it&apos;d move the needle for your team. Read in order — they
          build on each other.
        </p>
        <div className="mt-7 flex flex-wrap gap-2">
          {MECHANISMS.map((m, i) => (
            <a
              key={m.id}
              href={`#${m.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 ring-1 ring-white/10 text-[11px] font-mono text-slate-300 hover:ring-emerald-400/50 hover:text-emerald-300 transition-colors"
              data-testid={`how-it-saves-jump-${m.id}`}
            >
              <span className="text-emerald-400">{i + 1}.</span>
              {m.title.split('—')[0].split('.')[1]?.trim() || m.id}
            </a>
          ))}
        </div>
      </section>

      {/* Mechanism cards */}
      <section className="px-4 pb-16 max-w-3xl mx-auto space-y-8" data-testid="how-it-saves-mechanisms">
        {MECHANISMS.map((m) => {
          const Icon = m.icon;
          const cls = colorMap[m.color];
          return (
            <article
              key={m.id}
              id={m.id}
              className={`rounded-2xl bg-slate-900 ring-1 ${cls.border} p-6 sm:p-8 scroll-mt-24`}
              data-testid={`mechanism-${m.id}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${cls.iconBg}`}>
                  <Icon className="h-6 w-6" strokeWidth={1.8} />
                </div>
                <h2 className={`text-xl sm:text-2xl font-extrabold tracking-tight ${cls.accent} flex-1`}>
                  {m.title}
                </h2>
              </div>

              <div className="pl-0 sm:pl-16 space-y-4">
                {/* What it does */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400 mb-1">What it does</div>
                  <p className="text-sm text-slate-200 leading-relaxed">{m.plain_english}</p>
                </div>

                {/* Analogy */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400 mb-1">Analogy</div>
                  <p className="text-sm text-slate-300 italic">{m.analogy}</p>
                </div>

                {/* Concrete example */}
                <div className={`rounded-xl bg-slate-950 ring-1 ${cls.border} p-4`}>
                  <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400 mb-2">Concrete example</div>
                  <div className="space-y-2 text-[13px]">
                    <div className="flex items-start gap-2">
                      <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase bg-rose-500/10 text-rose-300 border border-rose-500/30">Without</span>
                      <p className="text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: m.example.without.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                    </div>
                    <div className="flex items-start gap-2">
                      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${cls.iconBg} border ${cls.border}`}>With CARTIE</span>
                      <p className="text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: m.example.with.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                    </div>
                    <div className="flex items-start gap-2 pt-2 border-t border-white/5">
                      <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">Saves you</span>
                      <p className="text-emerald-200 leading-relaxed font-bold">{m.example.savings}</p>
                    </div>
                  </div>
                </div>

                {/* Where to verify */}
                <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2">
                  <ArrowRight className="w-3 h-3" />
                  See it in your dashboard at: <code className="text-emerald-300">{m.where}</code>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      {/* The math at the end */}
      <section className="px-4 pb-16 max-w-3xl mx-auto" data-testid="how-it-saves-rollup">
        <div className="rounded-2xl bg-slate-900 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-slate-900 ring-1 ring-emerald-500/40 p-8">
          <div className="flex items-center gap-3 mb-3">
            <DollarSign className="w-7 h-7 text-emerald-300" />
            <h2 className="text-2xl font-extrabold tracking-tight">The bottom line</h2>
          </div>
          <p className="text-sm text-slate-200 leading-relaxed max-w-2xl">
            Stacked together, CARTIE customers typically reclaim{' '}
            <strong className="text-emerald-300">20-40% of their combined cloud + AI bill</strong>
            {' '}— and we contractually guarantee at least <strong className="text-emerald-300">3× your subscription</strong>{' '}
            in verified savings within 90 days. Don&apos;t hit it? Get a full refund + we resign the contract.
          </p>
          <div className="mt-5 grid sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-slate-950 ring-1 ring-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400">Typical AI bill cut</div>
              <div className="text-2xl font-extrabold text-emerald-300 mt-1">30–70%</div>
            </div>
            <div className="rounded-lg bg-slate-950 ring-1 ring-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400">Typical cloud bill cut</div>
              <div className="text-2xl font-extrabold text-emerald-300 mt-1">15–25%</div>
            </div>
            <div className="rounded-lg bg-slate-950 ring-1 ring-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400">Time to first $ saved</div>
              <div className="text-2xl font-extrabold text-emerald-300 mt-1">&lt; 1 hour</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="px-4 pb-16 max-w-3xl mx-auto text-center" data-testid="how-it-saves-cta">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
          Ready to see your own numbers?
        </h2>
        <p className="mt-3 text-sm text-slate-400">
          5 minutes to connect. No credit card required.
        </p>
        <div className="mt-7 flex flex-wrap gap-3 justify-center">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 text-slate-950 text-sm font-bold hover:bg-emerald-400 transition-colors"
            data-testid="how-it-saves-cta-signup"
          >
            Start free <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 ring-1 ring-white/20 text-slate-100 text-sm font-semibold hover:bg-slate-800 transition-colors"
            data-testid="how-it-saves-cta-autonomy"
          >
            See the autonomy tiers (T4 → T1)
          </Link>
          <Link
            to="/guarantee"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 ring-1 ring-white/20 text-slate-100 text-sm font-semibold hover:bg-slate-800 transition-colors"
            data-testid="how-it-saves-cta-guarantee"
          >
            3× ROI policy
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 mt-4 px-4 py-8 text-center text-[11px] text-slate-500">
        <p>© 2026 CARTIEAI LLC · Texas, USA · No jargon. Just savings.</p>
      </footer>
    </div>
  );
}
